/**
 * @render react
 * @name Newsletter
*/

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withPolaris } from 'Components/protons/Polaris';

import { Button } from 'Components/atoms/Button';
import { Image } from 'Components/atoms/Image';
import { Input } from 'Components/atoms/Input';
import { Form } from 'Components/molecules/Form';
import { Heading } from 'Components/molecules/Heading';
import { Label } from 'Components/molecules/Label';
import { Link } from 'Components/molecules/Link';

import './_style.scss';

/**
 * Newsletter component
 * @param {Object} context - extra information coming from Polaris provider
 * @param {string} cta_label - label for the button, e.g. Sign me up
 * @param {Object} extraClassNames - extra classes to be applied to the component
 * @param {string} inputId - ID for the newsletter input field
 * @param {string} inputName - Name for the newsletter input field
 * @param {string} label - Label for the newsletter input
 * @param {string} labelScreenReaderText - Screen reader text for the newsletter label
 * @param {object} link - props to be passed to Link Component
 * @param {string} linkUrl - URL for the link if showLink is true
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {string} mainTitle  - main title for the component
 * @param {string} position - Position of the newsletter on the page ('header', 'footer', or 'inline')
 * @param {boolean} showLink - Whether to show a link instead of the input text
 * @param {number} size - supported tag size
 * @param {string} srLabel - aria-label string
 * @param {string} subtitle - sub title for the newsletter
 * @param {string} title - title for the newsletter
 */

class Newsletter extends PureComponent {
  /**
   * Constructor
   */
  constructor(props) {
    super(props);

    this.state = {
      email: '',
    };

    // Bind the methods
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  /**
   * onSubmit event handle. Will pass the entered email value to the parent component
   *
   * @param {SyntheticEvent} e - Event
   */
  async handleSubmit(e) {
    const { onSubmit } = this.props;
    const { value } = this.state;

    if (onSubmit) {
      onSubmit(e, value);
    }
  }

  /**
   * Handles the click event for the link.
   * @param {('header'|'footer'|'inline')} position - Position of the newsletter on the page
   */
  static handleLinkClick(position) {
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'newsletter_form.entry_point',
        'newsletter_form.position': position,
      });
    }
  }

  /**
   * Render the component
   */
  render() {
    const {
      context: { cssPrefix },
      cta_label,
      extraClassNames,
      image,
      imageSize,
      inputId,
      inputName,
      label,
      labelScreenReaderText,
      link,
      linkUrl,
      loadStyles,
      mainTitle,
      placeholder,
      position,
      showLink,
      srLabel,
      subtitle,
      title,
    } = this.props;
    const { email } = this.state;

    /**
     * Handles the email input change event
     * @param {SyntheticEvent} e - Trigerred event
     */
    const handleInputChange = (e) => {
      // eslint-disable-next-line
      const { target: { value: email } } = e;

      this.setState({
        email,
      });
    };

    return (
      <div
        className={classNames({
          [`${cssPrefix}__newsletter`]: loadStyles,
          ...extraClassNames,
        })}
      >
        <Form
          action="/newsletter-signup"
          onSubmit={this.handleSubmit}
          method="get"
        >
          {mainTitle
            && (
            <Heading
              size={4}
              extraClassNames={{ [`${cssPrefix}__newsletter--main-heading`]: true }}
            >
              <span>{mainTitle}</span>
            </Heading>
            )}
          <div className={`${cssPrefix}__newsletter--form`}>
            <div
              className={classNames({
                [`${cssPrefix}__newsletter--headings`]: true,
              })}
            >
              <Heading size={3} extraClassNames={{ [`${cssPrefix}__newsletter--title`]: true }}>{title}</Heading>

              {subtitle && <Heading size={4} extraClassNames={{ [`${cssPrefix}__newsletter--subtitle`]: true }}>{subtitle}</Heading>}
            </div>

            <div
              className={classNames({
                [`${cssPrefix}__newsletter--button-group`]: true,
                '-show-link': showLink,
              })}
            >
              {
                showLink
                  ? (
                    <Link
                      extraClassNames={{ [`${cssPrefix}__newsletter--link`]: true, [`${cssPrefix}__button`]: true }}
                      href={linkUrl}
                      title={link.title}
                      screenReaderText={link.screenReaderText || undefined}
                      onClick={(e) => {
                        e.preventDefault();
                        Newsletter.handleLinkClick(position);
                        if (linkUrl) {
                          window.location.href = linkUrl;
                        }
                      }}
                    >
                      {cta_label}
                    </Link>
                  )
                  : (
                    <>
                      <Label
                        content={label}
                        target={inputId || `${cssPrefix}__newsletter--input`}
                        className={`${cssPrefix}__newsletter--label`}
                      >
                        {labelScreenReaderText && (
                          <span className="screen-reader-text">{labelScreenReaderText}</span>
                        )}
                      </Label>
                      <Input
                        id={inputId || `${cssPrefix}__newsletter--input`}
                        type="email"
                        name={inputName}
                        placeholder={placeholder}
                        value={email}
                        onChange={handleInputChange}
                        srLabel={srLabel}
                      />
                      <Button
                        type="submit"
                        srLabel={srLabel}
                        label={cta_label}
                        onClick={() => {
                          Newsletter.handleLinkClick(position);
                        }}
                      />
                    </>
                  )
              }
            </div>
          </div>
        </Form>

        {image && image.src && (
          <div className={`${cssPrefix}__newsletter--image`}>
            <Image
              size={imageSize}
              {...image}
              displayImageMetadata
            />
          </div>
        )}
      </div>
    );
  }
}

/**
 * Valid props
 */
Newsletter.propTypes = {
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
  }),
  cta_label: PropTypes.string,
  extraClassNames: PropTypes.objectOf(PropTypes.bool),
  image: PropTypes.shape({
    alt: PropTypes.string,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    src: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  imageSize: PropTypes.string,
  inputId: PropTypes.string,
  inputName: PropTypes.string,
  label: PropTypes.string,
  labelScreenReaderText: PropTypes.string,
  link: PropTypes.shape(),
  linkUrl: PropTypes.string,
  loadStyles: PropTypes.bool,
  mainTitle: PropTypes.string,
  onSubmit: PropTypes.func,
  placeholder: PropTypes.string,
  position: PropTypes.string,
  showLink: PropTypes.bool,
  srLabel: PropTypes.string,
  subtitle: PropTypes.string,
  title: PropTypes.string,
};

/**
 * Default props
 */
Newsletter.defaultProps = {
  cta_label: 'Submit',
  context: {},
  extraClassNames: {},
  image: null,
  imageSize: 'subscription',
  inputId: null,
  inputName: 'mail',
  label: '',
  labelScreenReaderText: '',
  link: {},
  linkUrl: null,
  loadStyles: true,
  mainTitle: '',
  onSubmit: null,
  placeholder: '',
  position: '',
  showLink: false,
  srLabel: 'Subscribe',
  subtitle: '',
  title: '',
};

export default withPolaris(Newsletter);
